import { AccountLibraryActionTypes, AccountLibraryActions } from './account-library.actions';
import { AccountLibraryState, initialAccountLibraryState } from './account-library.state';
import { FormCategory, FormTemplate, SearchModel } from '@nrwl/rx-models';
import { SelectionModel } from '@angular/cdk/collections';

export function accountLibraryReducer(
  state = initialAccountLibraryState,
  action: AccountLibraryActions,
): AccountLibraryState {
  switch (action.type) {
    case AccountLibraryActionTypes.LoadAccountLibrary:
      return {
        ...state,
        isLoaded: false,
      };

    case AccountLibraryActionTypes.LoadAccountLibrarySuccess: {
      const templates = {};
      action.payload.categories.forEach((item) => (templates[`c${item.id}`] = item));

      action.payload.templates.forEach((temp: FormTemplate) => {
        if (
          templates[`c${(temp.category as FormTemplate).id}`] &&
          templates[`c${(temp.category as FormTemplate).id}`].form_templates
        ) {
          const addedTemplate = {
            ...temp,
            category: (temp.category as FormTemplate).id,
          };
          templates[`c${(temp.category as FormTemplate).id}`].form_templates = [
            ...templates[`c${(temp.category as FormTemplate).id}`].form_templates,
            addedTemplate,
          ];
        } else {
          const addedTemplate = {
            ...temp,
            category: (temp.category as FormTemplate).id,
          };
          templates[`c${(temp.category as FormTemplate).id}`] = {
            ...(temp.category as FormTemplate),
            form_templates: [addedTemplate],
          };
        }
      });
      const library = Object.values(templates).filter(
        (item: FormCategory | any) => item.form_templates && !!item.form_templates.length,
      );

      const newSelectedFormGroups = new SelectionModel<string>(true, [
        ...(state.selectedFormGroups['_selected'] || []),
      ]);

      library.forEach((item: FormCategory | any) => {
        newSelectedFormGroups.select('c' + item.id);
      });

      return {
        ...state,
        library,
        selectedFormGroups: newSelectedFormGroups,
        isLoaded: true,
        formCategories: action.payload.categories,
        formTemplates: action.payload.rawTemplates ?? action.payload.templates,
        formTemplatesTypeFilter: action.payload.typeFilter ?? null,
      };
    }

    case AccountLibraryActionTypes.ExpandFormGroup: {
      const newSelectedFormGroups = new SelectionModel<string>(true, [
        ...(state.selectedFormGroups['_selected'] || []),
      ]);

      if (newSelectedFormGroups.isSelected(action.payload)) {
        newSelectedFormGroups.deselect(action.payload);
      } else {
        newSelectedFormGroups.select(action.payload);
      }

      return {
        ...state,
        selectedFormGroups: newSelectedFormGroups,
      };
    }

    case AccountLibraryActionTypes.SelectFormTemplate: {
      const selectedTemplates = action.payload.form._selected
        ? [...state.selectedTemplates]
        : state.selectedTemplates.filter((form) => form.id !== action.payload.form.id);
      return {
        ...state,
        selectedTemplates: [...selectedTemplates],
      };
    }

    case AccountLibraryActionTypes.SelectFormTemplateSuccess: {
      const template = action.payload;
      return {
        ...state,
        selectedTemplates: [...state.selectedTemplates, template],
        editableTemplates:
          state.editableTemplates.length === 1 ? [template] : state.editableTemplates,
        previewTemplate: null,
      };
    }
    case AccountLibraryActionTypes.DeselectAllTemplates: {
      // Create new objects instead of modifying existing ones
      const updatedLibrary = state.library.map((item) => {
        // Create a new item with updated form_templates
        return {
          ...item,
          form_templates: item.form_templates.map((form) => {
            // Create a new form object with _selected set to false
            return {
              ...form,
              _selected: false,
            };
          }),
        };
      });
      return {
        ...state,
        library: updatedLibrary,
        selectedTemplates: [],
        editableTemplates: [],
        previewTemplate: null,
        error: '',
      };
    }

    case AccountLibraryActionTypes.SearchForm: {
      const keyword = action.payload.keyword.toLowerCase();
      const search = new SearchModel(keyword);

      const newSelectedFormGroups = new SelectionModel<string>(true, []);

      // Copy existing selections
      if (state.selectedFormGroups) {
        const currentSelections = Array.from(state.selectedFormGroups['_selected'] || []);
        currentSelections.forEach((id: any) => newSelectedFormGroups.select(id));
      }

      search.result = state.library
        .map((category) => {
          const form_templates = category.form_templates.filter((formTemplate) =>
            formTemplate.name.toLowerCase().includes(keyword),
          );
          if (category.form_templates.length) {
            // Use the new selection model
            newSelectedFormGroups.select('c' + category.id);
          }
          return { ...category, form_templates };
        })
        .filter((formGroup) => formGroup.form_templates.length);

      return {
        ...state,
        search,
        selectedFormGroups: newSelectedFormGroups,
        error: '',
      };
    }

    case AccountLibraryActionTypes.ResetSearchForm:
      return {
        ...state,
        search: new SearchModel(''),
        error: '',
      };

    case AccountLibraryActionTypes.SetEditableTemplates: {
      const form = action.payload.length
        ? state.selectedTemplates.find((item) => item.id === action.payload[0].id)
        : null;
      const editableTemplates = action.payload.length
        ? action.payload.length > 1
          ? state.selectedTemplates.filter(
              (item) => item.type !== 1 && action.payload.find((temp) => item.id === temp.id),
            )
          : form
            ? [form]
            : action.payload
        : [];

      return {
        ...state,
        editableTemplates: [...editableTemplates],
        selectedTemplates: [...state.selectedTemplates],
      };
    }

    case AccountLibraryActionTypes.SetEditableTemplatesSuccess: {
      const selected = [...state.selectedTemplates];
      return {
        ...state,
        editableTemplates: [...action.payload],
        selectedTemplates: [...selected],
      };
    }

    case AccountLibraryActionTypes.SetAppointment:
      return {
        ...state,
        appointment: action.payload.appointment ?? null,
      };

    case AccountLibraryActionTypes.ClearAppointment:
      return {
        ...state,
        appointment: null,
      };

    case AccountLibraryActionTypes.SaveEditableTemplates: {
      const editableTemplates = state.editableTemplates.map((form) => {
        // Need an additional check

        // const savedForm = state.savedForms.find((item) => item.template.id === action.payload.id);
        // if(savedForm) {
        //     form.template = action.payload.;
        // } else {
        if (form.id === action.payload.id) {
          form.template = action.payload.template;
        }
        //}

        return form;
      });

      const previewTemplate = state.previewTemplate
        ? {
            ...state.previewTemplate,
            template: action.payload.template,
          }
        : state.previewTemplate;

      return {
        ...state,
        editableTemplates: [...editableTemplates],
        selectedTemplates: [...editableTemplates],
        previewTemplate,
      };
    }

    case AccountLibraryActionTypes.FillSign: {
      return {
        ...state,
      };
    }

    case AccountLibraryActionTypes.FillSignSuccess: {
      const selectedTemplates = state.selectedTemplates.map((form) =>
        form.id === action.payload.template.id ? { ...form, ...action.payload.template } : form,
      );

      // Create new objects instead of modifying existing ones
      const library = state.library.map((category) => {
        return {
          ...category,
          forms: category.form_templates.map((form) => {
            // Create a new form object with _selected set to false
            return form.id === action.payload.template.id
              ? { ...form, ...action.payload.template, _selected: false }
              : { ...form, _selected: false };
          }),
        };
      });

      const savedForms = state.savedForms.map((form) =>
        form.id === action.payload.id ? { ...form, form_data: action.payload.form_data } : form,
      );

      const isFormExisting = state.savedForms.some((form) => form.id === action.payload.id);

      if (!isFormExisting) {
        savedForms.push(action.payload);
      }

      return {
        ...state,
        library,
        selectedTemplates,
        savedForms,
      };
    }

    case AccountLibraryActionTypes.FinalisationFillSign:
      return {
        ...state,
      };

    case AccountLibraryActionTypes.FinalisationFillSignSuccess:
      return {
        ...state,
        selectedTemplates: [],
        editableTemplates: [],
        previewTemplate: null,
      };

    case AccountLibraryActionTypes.FillSignReset:
      return {
        ...state,
        selectedTemplates: [],
        editableTemplates: [],
        savedForms: [],
        previewTemplate: null,
        error: '',
      };

    case AccountLibraryActionTypes.SetPreviewTemplate:
      return {
        ...state,
      };

    case AccountLibraryActionTypes.SetPreviewTemplateSuccess: {
      const completed_at = state.library
        .find((category) => category.id === action.payload.category)
        ?.form_templates?.find((temp) => temp.id === action.payload.id)?.completed_at;

      const templateRes = {
        ...action.payload,
        completed_at,
      };

      return {
        ...state,
        previewTemplate: templateRes,
      };
    }

    case AccountLibraryActionTypes.ShareViaPortal:
      return {
        ...state,
      };

    case AccountLibraryActionTypes.ShareSuccess: {
      const selectedTemplates = state.selectedTemplates.map((form) =>
        form.id === action.payload.template ? { ...form, ...action.payload.template } : form,
      );

      // Create new objects instead of modifying existing ones
      const library = state.library.map((category) => {
        return {
          ...category,
          forms: category.form_templates.map((form) => {
            // Create a new form object with _selected set to false
            return form.id === action.payload.template
              ? { ...form, ...action.payload.template, _selected: false }
              : { ...form, _selected: false };
          }),
        };
      });

      return {
        ...state,
        library,
        selectedTemplates,
        editableTemplates: [],
      };
    }

    // #region Error

    case AccountLibraryActionTypes.Error:
      return {
        ...state,
        error: action.payload,
      };

    default:
      return state;
  }
}
